<template>
    <section>
        <h2 class="title text-ink mb-1">Question by question</h2>
        <p class="body-small text-ink-muted mb-6">Ratings and response insights, grouped by section.</p>

        <div class="flex flex-col gap-10">
            <!-- Top-level sections; each renders its own header + (optionally) sub-section bands. -->
            <div v-for="topSection in topLevelSections" :key="topSection.section_id">
                <header class="mb-4">
                    <div class="flex items-end justify-between gap-4">
                        <div>
                            <h3 class="title-body text-ink">{{ topSection.title }}</h3>
                            <p v-if="topSection.description" class="body-small text-ink-muted mt-1">{{ topSection.description }}</p>
                        </div>
                        <div v-if="topSection.avg !== null" class="text-right shrink-0">
                            <div class="details uppercase tracking-wide text-ink-muted">{{ isSelfAssessmentOnly ? ratingLabel : "Section avg" }}</div>
                            <div class="title-body text-ink">{{ formatNumber(topSection.avg, "one-decimal") }}</div>
                            <div v-if="topSection.self_avg !== null" class="details text-ink-muted mt-1">Self: {{ formatNumber(topSection.self_avg, "one-decimal") }}</div>
                        </div>
                    </div>
                </header>

                <!-- A top-level section either has child sub-sections OR its own direct questions. -->
                <template v-if="childrenOf(topSection.section_id).length > 0">
                    <div v-for="child in childrenOf(topSection.section_id)" :key="child.section_id" class="mb-6">
                        <div class="flex items-end justify-between gap-4 mb-3">
                            <h4 class="body-small-bold uppercase tracking-wider text-ink">{{ child.title }}</h4>
                            <div v-if="child.avg !== null" class="details text-ink-muted">
                                {{ isSelfAssessmentOnly ? ratingLabel : "Avg" }}
                                <span class="font-semibold text-ink">{{ formatNumber(child.avg, "one-decimal") }}</span>
                            </div>
                        </div>
                        <div class="flex flex-col gap-5">
                            <QuestionCard
                                v-for="(q, idx) in questionsForSection(child.section_id)"
                                :key="q.question_id"
                                :question="q"
                                :ordinal="ordinalFor(q)"
                                :subject-first-name="props.subjectFirstName"
                                :viewer-is-subject="props.viewerIsSubject"
                                :anonymity-threshold="props.anonymityThreshold"
                                :is-self-assessment-only="isSelfAssessmentOnly"
                                :idx="idx" />
                        </div>
                    </div>
                </template>
                <div v-else class="flex flex-col gap-5">
                    <QuestionCard
                        v-for="(q, idx) in questionsForSection(topSection.section_id)"
                        :key="q.question_id"
                        :question="q"
                        :ordinal="ordinalFor(q)"
                        :subject-first-name="props.subjectFirstName"
                        :viewer-is-subject="props.viewerIsSubject"
                        :anonymity-threshold="props.anonymityThreshold"
                        :is-self-assessment-only="isSelfAssessmentOnly"
                        :idx="idx" />
                </div>
            </div>

            <!-- Any orphan questions (no section, or section not in the aggregates block) — render at the end. -->
            <div v-if="orphanQuestions.length > 0" class="flex flex-col gap-5">
                <QuestionCard
                    v-for="(q, idx) in orphanQuestions"
                    :key="q.question_id"
                    :question="q"
                    :ordinal="ordinalFor(q)"
                    :subject-first-name="props.subjectFirstName"
                    :viewer-is-subject="props.viewerIsSubject"
                    :anonymity-threshold="props.anonymityThreshold"
                    :is-self-assessment-only="isSelfAssessmentOnly"
                    :idx="idx" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";

import QuestionCard from "./QuestionByQuestionCard.vue";
import type { PerQuestion, SectionAggregate } from "./types";

interface Props {
    questions: PerQuestion[];
    sections?: SectionAggregate[];
    subjectFirstName: string;
    // False when a team lead is viewing; second-person labels become third person.
    viewerIsSubject?: boolean;
    anonymityThreshold: number;
    // LAB-795: relabel aggregate wording ("avg") to "Your rating" for a
    // self-assessment-only cycle, which has no raters to average over.
    isSelfAssessmentOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    sections: () => [],
    isSelfAssessmentOnly: false,
    viewerIsSubject: true,
});

const ratingLabel = computed(() => (props.viewerIsSubject ? "Your rating" : `${props.subjectFirstName}'s rating`));

// Stable global ordinal so each question keeps a "Q1, Q2, Q3..." label even
// when grouped by section.
const ordinalById = computed<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    props.questions.forEach((q, i) => {
        map[q.question_id] = i + 1;
    });
    return map;
});

function ordinalFor(q: PerQuestion): number {
    return ordinalById.value[q.question_id] ?? 0;
}

const topLevelSections = computed(() => props.sections.filter((s) => s.depth === 0).sort((a, b) => a.order - b.order));

function childrenOf(sectionId: string): SectionAggregate[] {
    return props.sections.filter((s) => s.parent_section_id === sectionId).sort((a, b) => a.order - b.order);
}

function questionsForSection(sectionId: string): PerQuestion[] {
    return props.questions.filter((q) => q.section_id === sectionId);
}

const sectionIds = computed(() => new Set(props.sections.map((s) => s.section_id)));

const orphanQuestions = computed(() => props.questions.filter((q) => !q.section_id || !sectionIds.value.has(q.section_id)));
</script>
